import React, { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import {
  getReservation,
  deleteReservation,
  updateReservation,
} from "../../api/reservation.api";
import { toast } from "react-toastify";
import { sentenceCase, titleCase } from "../../utils/case";
import { formatDateTime, formatTime } from "../../utils/datetime";

const ReservationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getReservation(id);
        setReservation(res);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!reservation) {
    return <div className="text-center mt-5">Reservation not found</div>;
  }

  const handleUpdate = async (payload, successMessage) => {
    try {
      await updateReservation(id, payload);
      toast.success(successMessage);
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Fragment>
        <style>
          {`
            @media print {
              body * {
                visibility: hidden;
              }

              #print-section,
              #print-section * {
                visibility: visible;
              }

              #print-section {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
              }

              /* 🔥 REMOVE SIDEBAR / LAYOUT OFFSET */
              .page-wrapper,
              .page-body-wrapper,
              .page-body,
              .sidebar-wrapper,
              .sidebar-main,
              .sidebar {
                all: unset !important;
              }

              /* Ensure full width */
              #print-section {
                width: 100vw !important;
                margin: 0 !important;
                padding: 0 !important;
              }

              /* Remove container padding */
              .container,
              .container-fluid {
                padding: 0 !important;
                margin: 0 !important;
                max-width: 100% !important;
              }

              /* Remove buttons */
              button {
                display: none !important;
              }

              /* Clean card look */
              .card {
                box-shadow: none !important;
                border: 1px solid #ddd;
              }

              @page {
                size: A4 landscape;
                margin: 10mm;
              }

              .print-txn-pdf {
                width: 100% !important;
                min-height: 600px !important; /* A4 height minus margins */
                display: block !important;
                margin: 0 auto !important;
              }

              .print-rcpt-pdf {
                width: 80% !important;
                min-height: 400px !important; /* A4 height minus margins */
                display: block !important;
                margin: 0 auto !important;
              }

              .print-new-page {
                page-break-before: always;
                break-before: page;
              }
              
              .print-transaction-slip {
                padding: 1px !important;
              }
            }
          `}
          </style>
      {/* ================= BREADCRUMBS ================= */}
      <Breadcrumbs
        parent={titleCase(t("reservation"))}
        title={titleCase(t("reservation details"))}
        mainTitle={`${reservation.bookingRef} (${titleCase(t(reservation.type.replace(/_/g, " ")))})`}
      />

      <Container fluid>
        {/* ================= ACTION BAR ================= */}
        <Row className="mb-4">
          <Col className="d-flex justify-content-end align-items-center gap-2 flex-wrap">

            {/* Confirm Reservation (only if pending) */}
            {reservation.status === "pending" && (
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() =>
                  handleUpdate(
                    { status: "confirmed" },
                    t("reservation confirmed")
                  )
                }
              >
                {titleCase(t("confirm reservation"))}
              </button>
            )}

            {reservation.status !== "cancelled" && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={async () => {
                  const confirmed = window.confirm(
                    t("are you sure you want to cancel this reservation?")
                  );

                  if (!confirmed) return;

                  handleUpdate(
                    { status: "cancelled" },
                    t("reservation cancelled")
                  );
                }}
              >
                {titleCase(t("cancel reservation"))}
              </button>
            )}

            {/* Divider */}
            <div
              style={{
                width: "1px",
                height: "24px",
                backgroundColor: "#ddd",
                margin: "0 8px",
              }}
            />

            {/* Print */}
            <button
              className="btn btn-sm btn-info"
              onClick={() => window.print()}
            >
              {titleCase(t("print"))}
            </button>

            {/* Edit */}
            <button
              className="btn btn-sm btn-warning"
              onClick={() => navigate(`/reservations/edit/${id}`)}
            >
              {titleCase(t("edit"))}
            </button>

            {/* Delete */}
            <button
              className="btn btn-sm btn-danger"
              disabled={deleting}
              onClick={async () => {
                const confirmed = window.confirm(
                  t("are you sure you want to delete this reservation?")
                );

                if (!confirmed) return;

                try {
                  setDeleting(true);
                  await deleteReservation(id);
                  toast.success(t("deleted successfully"));
                  navigate("/reservations");
                } catch (err) {
                  toast.error(err.message);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? t("deleting...") : titleCase(t("delete"))}
            </button>

            {/* Back */}
            <button
              className="btn btn-sm btn-dark"
              onClick={() => navigate(-1)}
            >
              {titleCase(t("back"))}
            </button>
          </Col>
        </Row>
        
        {/* PRINT AREA */}
        <div id="print-section">
          {/* ================= MAIN DETAILS (SINGLE CARD) ================= */}
          <Row>
            <Col md={6}>
              <Row>
                <Col>
                  <Card className="mb-4 shadow-sm">
                    <CardHeader>
                      <H5 className="mb-0 d-flex align-items-center gap-2">
                        <span className="m-r-15">
                          {titleCase(t("reservation details"))}
                        </span>

                        {/* Reservation Status Badge */}
                        <Badge
                          color={
                            reservation.status === "confirmed"
                              ? "success"
                              : reservation.status === "cancelled"
                              ? "danger"
                              : "warning"
                          }
                        >
                          {titleCase(t(reservation.status))}
                        </Badge>
                      </H5>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        {/* LEFT: Venue, Date & Time */}
                        <Col md="4">
                          <p>
                            <strong>{titleCase(t("venue"))}:</strong>{" "}
                            {reservation.venue?.name}
                          </p>
                          <p>
                            <strong>{titleCase(t("date"))}:</strong>{" "}
                            {reservation.date}
                          </p>
                          <p>
                            <strong>{titleCase(t("time"))}:</strong>{" "}
                            {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                          </p>
                          <p>
                            <strong>{titleCase(t("purpose"))}:</strong>{" "}
                            {reservation.purpose || "-"}
                          </p>
                          <p>
                            <strong>{titleCase(t("special request"))}:</strong>{" "}
                            {reservation.specialRequest || "-"}
                          </p>
                        </Col>

                        {/* MIDDLE: Name, Phone, Email */}
                        <Col md="4">
                          <p>
                            <strong>{titleCase(t("name"))}:</strong>{" "}
                            {reservation.name}
                          </p>
                          <p>
                            <strong>{titleCase(t("phone number"))}:</strong>{" "}
                            {reservation.phoneNo}
                          </p>
                          <p>
                            <strong>{titleCase(t("email"))}:</strong>{" "}
                            {reservation.email}
                          </p>
                        </Col>

                        {/* RIGHT: Type, Purpose, Special Request, Status */}
                        <Col md="4">
                          <p>
                            <strong>{titleCase(t("Booked At"))}:</strong>{" "}
                            {formatDateTime(reservation.createdAt) || "-"}
                          </p>
                          <p>
                            <strong>{titleCase(t("Last Updated At"))}:</strong>{" "}
                            {formatDateTime(reservation.updatedAt) || "-"}
                          </p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              {reservation.transaction?.paymentProof && (
              <Row>
                <Col>
                  {/* ================= TRANSACTION DETAILS ================= */}
                  {reservation.transaction && (
                    <Card className="mb-4 shadow-sm">
                      <CardHeader className="d-flex justify-content-between align-items-center">
                        <H5 className="mb-0">
                          <span className="m-r-15">
                            {titleCase(t("transaction details"))}
                          </span>
                          {/* Payment Status Badge (only when pending) */}
                          {reservation.paymentStatus === "pending" && (
                            <Badge color="warning">
                              {titleCase(t(reservation.paymentStatus.replace(/_/g, " ")))}
                            </Badge>
                          )}
                        </H5>
                        
                        
                      </CardHeader>

                      <CardBody>
                        <Row>
                          <Col md="6">
                            <p>
                              <strong>{titleCase(t("transaction ref"))}:</strong>{" "}
                              {reservation.transaction?.refNo ?? reservation.transaction?.id}
                            </p>
                            <p>
                              <strong>{titleCase(t("amount"))}:</strong>{" "}
                              {reservation.transaction?.amount}
                            </p>
                            {reservation.transaction?.receipt &&
                              <p>
                                <strong>{titleCase(t("Receipt No"))}:</strong>{" "}
                                {reservation.transaction?.receipt?.receiptNumber}
                              </p> 
                            }
                          </Col>

                          <Col md="6">
                            <p>
                              <strong>{titleCase(t("payment method"))}:</strong>{" "}
                              {sentenceCase(t(reservation.transaction?.paymentChannel || "-"))}
                            </p>
                            <p>
                              <strong>{titleCase(t("status"))}:</strong>{" "}
                              {titleCase(t(reservation.transaction?.status))}
                            </p>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  )}
                </Col>
              </Row>
              )}
            </Col>
            <Col md={6}>
              {reservation.transaction?.paymentProof && (
                <Card className="mb-4 shadow-sm">
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <H5 className="mb-0">
                      {titleCase(t("transaction slip"))}
                    </H5>
                    <div className="d-flex align-items-center gap-2">
                      {/* Confirm Payment (only if pending) */}
                      {reservation.transaction?.status === "pending" && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() =>
                            handleUpdate(
                              { paymentStatus: "paid" },
                              t("payment confirmed")
                            )
                          }
                        >
                          {titleCase(t("confirm payment"))}
                        </button>
                      )}

                    </div>
                  </CardHeader>

                  <CardBody className=" print-transaction-slip">
                    {reservation.transaction?.paymentProof ? (
                      (() => {
                        const fileUrl = `https://bfiles.chinfumc.org.my${reservation.transaction.paymentProof}`;
                        const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(
                          reservation.transaction.paymentProof
                        );
                        const isPdf = /\.pdf$/i.test(
                          reservation.transaction.paymentProof
                        );

                        if (isImage) {
                          return (
                            <a href={fileUrl} target="_blank" rel="noreferrer">
                              <img
                                src={fileUrl}
                                alt="Transaction Slip"
                                style={{
                                  width: "100%",
                                  maxHeight: "80vh",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                }}
                              />
                            </a>
                          );
                        }

                        if (isPdf) {
                          return (
                            <iframe
                              className="print-txn-pdf"
                              src={fileUrl}
                              title="Transaction Slip PDF"
                              style={{ width: "100%", height: "500px", border: "none" }}
                            />
                          );
                        }

                        return (
                          <a href={fileUrl} target="_blank" rel="noreferrer">
                            {fileUrl}
                          </a>
                        );
                      })()
                    ) : (
                      <p className="text-muted mb-0">
                        {titleCase(t("no transaction slip"))}
                      </p>
                    )}
                  </CardBody>
                </Card>
              )}
              {reservation.transaction && !reservation.transaction?.paymentProof && (
                <Card className="mb-4 shadow-sm">
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <H5 className="mb-0">
                      <span className="m-r-15">
                        {titleCase(t("transaction details"))}
                      </span>
                      {/* Payment Status Badge (only when pending) */}
                      {reservation.paymentStatus === "pending" && (
                        <Badge color="warning">
                          {titleCase(t(reservation.paymentStatus.replace(/_/g, " ")))}
                        </Badge>
                      )}
                    </H5>
                    
                    
                  </CardHeader>

                  <CardBody>
                    <Row>
                      <Col md="6">
                        <p>
                          <strong>{titleCase(t("transaction Ref"))}:</strong>{" "}
                          {reservation.transaction?.refNo ?? reservation.transaction?.id}
                        </p>
                        <p>
                          <strong>{titleCase(t("RRefNo"))}:</strong>{" "}
                          {reservation.transaction?.rrefNo}
                        </p>
                        <p>
                          <strong>{titleCase(t("amount"))}:</strong>{" "}
                          {reservation.transaction?.amount}
                        </p>
                        {reservation.transaction?.receipt &&
                          <p>
                            <strong>{titleCase(t("Receipt No"))}:</strong>{" "}
                            {reservation.transaction?.receipt?.receiptNumber}
                          </p> 
                        }
                      </Col>

                      <Col md="6">
                        <p>
                          <strong>{titleCase(t("payment method"))}:</strong>{" "}
                          {sentenceCase(t(reservation.transaction?.paymentChannel || "-"))}
                        </p>
                        <p>
                          <strong>{titleCase(t("status"))}:</strong>{" "}
                          {titleCase(t(reservation.transaction?.status))}
                        </p>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
          {reservation.transaction && (
          <Row className="print-new-page">
            {/* RIGHT: Receipt */}
            <Col>
              <Card className="mb-4 shadow-sm">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <H5 className="mb-0">
                    {titleCase(t("receipt"))} ({reservation.transaction?.receipt?.receiptNumber})
                  </H5>
                </CardHeader>

                <CardBody>
                  {reservation.transaction?.receipt?.documentUrl ? (
                    <iframe
                      className="print-rcpt-pdf"
                      src={`https://bfiles.chinfumc.org.my${reservation.transaction.receipt.documentUrl}`}
                      title="Receipt"
                      style={{ width: "100%", height: "500px", border: "none" }}
                    />
                  ) : (
                    <p className="text-muted mb-0">
                      {titleCase(t("no receipt"))}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default ReservationView;